import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function CTASection() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('cta-section');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return (
    <section id="cta-section" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 p-16">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          </div>

          {/* Content */}
          <div className={`relative z-10 text-center max-w-3xl mx-auto transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/20 backdrop-blur-md rounded-full text-white mb-8 border border-white/30">
              <i className="ri-rocket-line"></i>
              <span className="text-sm font-semibold">Get Started Today</span>
            </div>

            <h2 className="text-5xl font-extrabold text-white mb-6 leading-tight">
              Start Building Your Dream App Today
            </h2>
            
            <p className="text-xl text-white/95 mb-12 leading-relaxed">
              Join 10,000+ creators who have already built successful apps with Your App Wizard. No credit card required to start.
            </p>

            {/* CTA Buttons */}
            <div className="flex items-center justify-center gap-5 mb-12">
              <Link
                to="/get-started"
                className="group px-10 py-4 bg-white text-purple-600 rounded-lg hover:bg-gray-100 transition-all shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer flex items-center gap-3 font-bold hover:scale-105"
              >
                Start Building Free
                <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
              </Link>
              <Link
                to="/pricing"
                className="px-10 py-4 bg-white/10 backdrop-blur-md text-white rounded-lg hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer font-bold border-2 border-white/30 hover:scale-105"
              >
                View Pricing
              </Link>
            </div>

            {/* Features List */}
            <div className="flex items-center justify-center gap-8 text-white/90">
              <div className="flex items-center gap-2">
                <i className="ri-check-line text-xl"></i>
                <span className="text-sm font-medium">No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-check-line text-xl"></i>
                <span className="text-sm font-medium">Free templates included</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-check-line text-xl"></i>
                <span className="text-sm font-medium">Cancel anytime</span>
              </div>
            </div>
          </div>

          {/* Decorative Elements */}
          <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
          <div className="absolute -top-8 -right-8 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
        </div>

        {/* Trust Badges */}
        <div className={`mt-16 flex items-center justify-center gap-12 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <i className="ri-shield-check-line text-2xl text-green-600"></i>
            </div>
            <div>
              <div className="font-bold text-gray-900">Secure & Safe</div>
              <div className="text-sm text-gray-600">Enterprise-grade security</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <i className="ri-customer-service-line text-2xl text-blue-600"></i>
            </div>
            <div>
              <div className="font-bold text-gray-900">24/7 Support</div>
              <div className="text-sm text-gray-600">Always here to help</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-2xl text-purple-600"></i>
            </div>
            <div>
              <div className="font-bold text-gray-900">Money-Back</div>
              <div className="text-sm text-gray-600">30-day guarantee</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}